<template>
	<div class="w-full flex flex-col items-start hierarchy-select">
		<ul class="w-full flex flex-column">
			<li
				v-if="withSelectAll && level === 1"
				class="w-full list-unstyled flex flex-col my-1"
			>
				<div
					class="w-full flex items-center"
					@click.stop.prevent="CheckAll()"
				>
					<div class="flex-1 form-check py-1 level-1-color">
						<input
							id="select-all"
							type="checkbox"
							class=""
							name="select-all"
							hidden
							:checked="selectAll"
							@change="CheckAll()"
						/>
						<label
							class="form-check-label"
							for="select-all"
						>
						</label>
						<span class="flex-1 text-uppercase text-ellipsis font-bold font-level font-level-1">
							TODOS
						</span>
					</div>
				</div>
			</li>
			<li
				v-for="(item, index) in value"
				:key="`${item.identifier}-${item.id}-${index}`"
				class="w-full list-unstyled flex flex-col my-1"
			>
				<div
					class="flex items-center"
					@click.stop.prevent="ClickItem(item)"
				>
					<div
						class="flex-1 form-check py-1"
						:class="[
							`level-${level}-color`,
							{ 'hierarchy-group-label': selectionMode === 'radio' && item.children?.length },
						]"
					>
						<input
							v-if="selectionMode === 'checkbox' || !item.children?.length"
							:id="`${item.identifier}-${item.id}-${index}`"
							:type="selectionMode"
							class=""
							:name="selectionMode === 'radio' ? item.radioGroupName || radioGroupName : `${item.identifier}-${item.id}-${index}`"
							hidden
							:checked="item.selected"
							@change="SelectItem(item)"
						/>
						<label
							v-if="selectionMode === 'checkbox' || !item.children?.length"
							class="form-check-label"
							:for="`${item.identifier}-${item.id}-${index}`"
							@click.stop.prevent="SelectItem(item)"
						>
						</label>
						<span
							class="flex-1 text-uppercase text-ellipsis font-bold font-level"
							:class="`font-level-${level}`"
						>
							{{ item.name }}
							<span
								v-if="required && level === 1"
								class="required-mark"
							>
								*
							</span>
						</span>
					</div>
					<div
						v-if="item.children?.length"
						class="pr-3 cursor-pointer expand-indicator-container"
					>
						<i
							:class="{
								'fal fa-chevron-right': !item.expanded,
								'fal fa-chevron-down': item.expanded,
							}"
						></i>
					</div>
				</div>
				<div
					v-if="item.children?.length"
					class="children ml-5"
				>
					<HierarchySelect
						v-if="item.expanded"
						:value="item.children"
						:key-name="item.key"
						:identifier="item.identifier"
						:level="level + 1"
						:selection-mode="selectionMode"
						:radio-group-name="radioGroupName"
						:required="required"
						@change="$emit('change')"
						@select="$emit('select', $event)"
					/>
				</div>
			</li>
		</ul>
	</div>
</template>

<script>
	import { useChangeSelectedRecursively, useEachRecursively } from '@/composables'

	export default {
		name: 'HierarchySelect',
		props: {
			value: {
				type: Array,
				required: true,
			},
			keyName: {
				type: String,
				required: true,
			},
			identifier: {
				type: String,
				required: true,
			},
			level: {
				type: Number,
				default: 1,
			},
			withSelectAll: {
				type: Boolean,
				default: true,
			},
			selectionMode: {
				type: String,
				default: 'checkbox',
				validator: value => ['checkbox', 'radio'].includes(value),
			},
			radioGroupName: {
				type: String,
				default: '',
			},
			required: {
				type: Boolean,
				default: false,
			},
		},
		data() {
			return {
				pauseWatcher: false,
				selectAll: false,
			}
		},
		watch: {
			value: {
				handler(val) {
					if (this.pauseWatcher) return

					this.UpdateParentSelectionState()
				},
				immediate: true,
				deep: true,
			},
		},
		methods: {
			ClickItem(item) {
				if (item.children?.length) {
					item.expanded = !item.expanded
					this.$emit('change')
				} else {
					this.SelectItem(item)
				}
			},
			SelectItem(item) {
				if (this.selectionMode === 'radio') {
					item.selected = true
					this.$emit('select', item)
					this.$emit('change')
					return
				}

				this.ChangeSelect(item)
			},
			async ChangeSelect(item) {
				this.pauseWatcher = true

				const newState = !item.selected
				item.selected = newState

				if (item.children?.length) {
					item.children = useChangeSelectedRecursively(item.children, newState)
				}

				await this.$nextTick()

				this.UpdateParentSelectionState()

				this.pauseWatcher = false

				this.$emit('change')
			},
			UpdateParentSelectionState() {
				this.value.forEach(i => {
					if (i.children?.length) {
						i.selected = i.children?.some(c => c.selected)
					}
				})
			},
			async CheckAll() {
				const newState = !this.selectAll
				this.selectAll = newState

				await useEachRecursively(this.value, item => {
					item.selected = newState
				})

				this.$emit('change')
			},
		},
	}
</script>

<style lang="scss" scoped>
	.expand-indicator-container {
		display: flex;
		align-items: center;
	}

	.form-check input:checked ~ label:after {
		background: var(--newPrimaryBackgroundColor) !important;
	}

	.required-mark {
		color: #eab308;
		margin-left: 2px;
	}
</style>
